import { useTask } from "@/composables/tasks/useTask";
import { computed, ComputedRef } from "vue";
import { isVaccinationTask } from "@/stores/v2/sites/utils";
import { VaccinationProgressEntry, VaccineConfiguration, WeightRecord } from "@/types/v2";

export function useWeight() {
  // ===================================================================
  // Store
  // ===================================================================
  const { activeTask } = useTask();

  // ===================================================================
  // Constants
  // ===================================================================
  const INDIVIDUAL_WEIGHTS = "individual-weights"
  const RANDOM_WEIGHTS = "random-weights";
  const NO_WEIGHTS = "no-weights"

  // ===================================================================
  // Validations
  // ===================================================================
  // Is configuration of individual weight type
  const isIndividualWeightType: ComputedRef<boolean> = computed(() => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value)) return false;
    return activeTask.value.details.weightConfiguration.type === INDIVIDUAL_WEIGHTS;
  })

  // Is configuration of no weights type
  const isNoWeightType: ComputedRef<boolean> = computed(() => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value)) return false;
    return activeTask.value.details.weightConfiguration.type === NO_WEIGHTS;
  })

  // Is configuration of random weights type
  const isRandomWeightType: ComputedRef<boolean> = computed(() => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value)) return false;
    return activeTask.value.details.weightConfiguration.type === RANDOM_WEIGHTS;
  })

  // Should the weighing progress be displayed
  const shouldDisplayProgress: ComputedRef<boolean> = computed(() => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value) || !getWeightType.value) return false;
    return (RANDOM_WEIGHTS === getWeightType.value) || (INDIVIDUAL_WEIGHTS === getWeightType.value);
  })

  // ===================================================================
  // Getters
  // ===================================================================
  // Get total number of weighed fish
  const getWeighedFish: ComputedRef<number> = computed(() => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value) || !getWeightType.value) return 0;

    if (!activeTask.value.details.weightConfiguration.progressData) {
      activeTask.value.details.weightConfiguration.progressData = []
    }

    return activeTask.value.details.weightConfiguration.progressData.length;
  });

  // Get total number of weighed fish in configuration
  const getWeighedFishInConfiguration = (configuration: VaccineConfiguration) => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value) || !isIndividualWeightType.value) return 0;

    if (!activeTask.value.details.weightConfiguration.progressData) {
      activeTask.value.details.weightConfiguration.progressData = [];
    }

    return activeTask.value.details.weightConfiguration.progressData
      .filter((record: WeightRecord) => record.configurationKey === configuration.key).length;
  };

  // Get Target Weight Amount
  const getTargetWeightAmount: ComputedRef<number | null> = computed(() => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value)) return null;

    const targetAmount = activeTask.value.details.weightConfiguration.targetWeightAmount;
    if(getWeightType.value === RANDOM_WEIGHTS && targetAmount) return targetAmount;

    return 0;
  })

  // Get Target Weight Amount for Vaccination Configuration
  const getTargetWeightAmountInConfiguration = (configuration: VaccineConfiguration) => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value) || !isIndividualWeightType.value) return null;

    return configuration.targetWeightAmount;
  };

  // Get average weight for Vaccination Records
  const getAverageWeightByConfigKeyVaccinationRecords = (key: string): number | string => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value)) return "No data";

    const configuration = activeTask.value.details.configurations.find((config) => {
      return config.key === key
    })
    if (!configuration || !configuration.progressData) return "No data";

    const records = configuration.progressData.filter((r: VaccinationProgressEntry) => r.weight);
    if (!records.length) return "No data";

    const sum = records.reduce((total: number, record: VaccinationProgressEntry) => total + record.weight, 0);
    return Math.round((sum / records.length) * 100) / 100;
  }

  // Get Weight Configuration Type
  const getWeightType: ComputedRef<string | null> = computed(() => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value)) return null;
    return activeTask.value.details.weightConfiguration.type;
  });

  // Get Weight Records
  const weightRecords: ComputedRef<WeightRecord[]> = computed(() => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value)) return [];
    return activeTask.value.details.weightConfiguration.progressData ?? [];
  })

  return {
    // ===================================================================
    // Validations
    // ===================================================================
    isIndividualWeightType,
    isNoWeightType,
    isRandomWeightType,
    shouldDisplayProgress,

    // ===================================================================
    // Getters
    // ===================================================================
    getWeighedFish,
    getWeightType,
    weightRecords,
    getTargetWeightAmount,
    getTargetWeightAmountInConfiguration,
    getWeighedFishInConfiguration,
    getAverageWeightByConfigKeyVaccinationRecords
  }
}